import React, { useMemo, useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { AgeCategory } from '../../types';

const FlavorSelect: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { recipes, t } = useAppConfig();

  // Sabores disponibles: filtra por etapa de vida (sin filtro de alergia).
  const available = useMemo(() => {
    const target = formData.ageCategory === AgeCategory.CACHORRO ? 'puppy' : 'adult';
    return recipes.filter((r) => !r.lifeStage || r.lifeStage === target || r.lifeStage === 'all');
  }, [recipes, formData.ageCategory]);

  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    // selectedRecipes are keyed by the canonical product.id (UUID).
    const chosen = new Set(formData.selectedRecipes || []);
    return Object.fromEntries(available.filter((r) => chosen.has(r.productId)).map((r) => [r.id, true]));
  });

  const persist = (map: Record<string, boolean>) => {
    const productIds = available.filter((r) => map[r.id]).map((r) => r.productId);
    updateFormData({ selectedRecipes: productIds });
  };

  const toggle = (id: string) => {
    const next = { ...selected, [id]: !selected[id] };
    setSelected(next);
    persist(next);
  };

  const anySelected = Object.values(selected).some(Boolean);
  const dogName = formData.nombre || 'tu perro';

  return (
    <FunnelLayout
      step={8}
      useTwoPhase
      onContinue={goToNextStep}
      onBack={goToPreviousStep}
      continueDisabled={!anySelected}
      continueDisabledReason={t('flavor_select.missing', 'Elige al menos un sabor')}
      onContinueDisabledAttempt={() => {}}
    >
      <div className="flex flex-col gap-4">
        <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-center text-black">
          {t('flavor_select.title', '¿De qué sabores quieres calcular la ración de {nombre}?', { nombre: dogName })}
        </h1>
        <p className="font-poppins text-sm text-center text-black/50">
          {t('flavor_select.subtitle', 'Elige uno o varios y te decimos los gramos de cada uno.')}
        </p>

        {available.length === 0 ? (
          <p className="font-poppins text-sm text-center text-black/50">
            {t('flavor_select.no_recipes', 'No hay sabores disponibles ahora mismo.')}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {available.map((r) => {
              const isSelected = !!selected[r.id];
              return (
                <div
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  className="bg-white rounded-2xl overflow-hidden flex flex-col items-center gap-2 p-3 text-center cursor-pointer transition-all relative"
                  style={{
                    border: isSelected ? '3px solid #008BBC' : '2px solid #e0e0e0',
                  }}
                  onClick={() => toggle(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(r.id);
                    }
                  }}
                >
                  {isSelected && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow z-20"
                      aria-hidden="true"
                    >
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}
                  {r.imageUrl ? (
                    <img src={r.imageUrl} alt="" className="w-20 h-20 object-contain" loading="lazy" />
                  ) : null}
                  <span className="font-fredoka text-sm font-semibold text-[#1a1a1a]">{r.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </FunnelLayout>
  );
};

export default FlavorSelect;
